import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BudgetSummaryModel } from '../../../../core/models/data/budget-summary.model';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { BudgetSummaryHeaderComponent } from '../../components/budget-summary-header/budget-summary-header.component';
import { BudgetSummaryChartComponent } from '../../components/budget-summary-chart/budget-summary-chart.component';
import { BudgetSummarySectionComponent } from '../../components/budget-summary-section/budget-summary-section.component';
import { AuditLogsComponent } from '../../../../parts/audit-logs/audit-logs.component';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { AuditEntityType } from '../../../../core/enums/base.enums';
import { DataEntityTypes, PropertyStatuses } from '../../../../core/enums/data.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';

@Component({
  selector: 'ms-budget-summary-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FaDuotoneIconComponent,
    BudgetSummaryHeaderComponent,
    BudgetSummaryChartComponent,
    BudgetSummarySectionComponent,
    AuditLogsComponent,
    IfHasPermissionDirective,
  ],
  templateUrl: './budget-summary.page.html',
  styleUrl: './budget-summary.page.scss',
})
export class BudgetSummaryPage implements OnInit {

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private financialService = inject(FinancialService);

  public budgetSummary = signal<BudgetSummaryModel | undefined>(undefined);
  public isLoading = signal<boolean>(false);
  public isBudgetDeleted = signal<boolean>(false);
  public budgetId: number | undefined;
  public activeTab = signal<'summary' | 'auditLog'>('summary');

  public AuditEntityType = AuditEntityType;
  public DataEntityTypes = DataEntityTypes;
  public FinancialsPermission = FinancialsPermission;

  public setActiveTab(tab: 'summary' | 'auditLog'): void {
    this.activeTab.set(tab);
  }

  public ngOnInit(): void {
    this.budgetId = +(this.activatedRoute.snapshot.paramMap.get('budgetId') ?? NaN) || undefined;

    if (!this.budgetId) {
      this.router.navigate(['system', '404']);
      return;
    }

    this.loadBudgetSummary();
  }

  private async loadBudgetSummary(): Promise<void> {
    this.isLoading.set(true);
    this.budgetSummary.set(undefined);

    try {
      const [summaryResponse, budgetResponse] = await Promise.all([
        this.financialService.getBudgetSummaryById(this.budgetId!),
        this.financialService.getBudgetById(this.budgetId!)
      ]);

      if (summaryResponse.success === true && summaryResponse.data) {
        this.budgetSummary.set(summaryResponse.data);
      } else {
        this.router.navigate(['system', '404']);
        return;
      }

      // Fail closed: only treat the budget as editable when we can confirm it isn't deleted.
      // If the status fetch failed, keep editing disabled rather than assuming it's allowed.
      const budget = budgetResponse.success ? budgetResponse.data : undefined;
      this.isBudgetDeleted.set(!budget || budget.status === PropertyStatuses.Deleted);
    } catch (error) {
      console.error('Error loading budget summary:', error);
      this.router.navigate(['system', '404']);
      return;
    } finally {
      this.isLoading.set(false);
    }
  }
}
